import React from "react";

import type {
	EightySixAction,
	EightySixPermissions,
	EightySixStatus,
	EightySixSubject,
} from "./eighty-six";

export interface EightySixContextValue {
	/** Keyed by `statusKey(productId, variantId)`. Absent means sellable. */
	statuses: Record<string, EightySixStatus>;
	permissions: EightySixPermissions;
	/** Applies one of the three writes, then refreshes. A no-op without a provider. */
	apply: (
		action: EightySixAction,
		subject: EightySixSubject,
		note?: string,
	) => Promise<void>;
	pending: boolean;
}

export const MISSING_PROVIDER =
	"EightySixMenu provider missing — a product tile cannot read 86 state without it";

/**
 * Turns "no provider" into a loud failure at first render.
 *
 * <para>Exported as a plain function so the guard can be tested by calling it with null, rather
 * than by standing up a DOM the terminal's node-environment vitest does not have.</para>
 */
export function requireEightySix(
	value: EightySixContextValue | null,
): EightySixContextValue {
	if (value === null) throw new Error(MISSING_PROVIDER);

	return value;
}

export const EightySixContext =
	React.createContext<EightySixContextValue | null>(null);

export function useEightySix(): EightySixContextValue {
	return requireEightySix(React.use(EightySixContext));
}
